import {
  Badge,
  Box,
  Icon,
  type Omittable,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useFitsBeside,
} from "@salestrak/ui";
import type React from "react";
import type { AccessLevel } from "#app/lib/utils/access-level";

/**
 * The access marker — the chip that tells a viewer their reach on the object
 * they have open stops short of editing, so the inert fields around it read as
 * a permission, not a bug.
 *
 * It wears the {@link SystemMarker} grammar (`Badge size="tag" tone="outline"`,
 * no pill, no color): the viewer's tier is a fact the system resolved, not a
 * status of the object, and a colored chip would claim a judgement it does not
 * carry. A lock glyph leads, the tier names itself tersely, and the tooltip
 * says only what is off-limits — a few words, not a sentence.
 *
 * It rides the header row beside the object's name and NEVER costs the name
 * width: when its labeled form would push the name into an ellipsis, it drops
 * to the lock alone (`useFitsBeside`), and the label survives as the chip's
 * accessible name.
 */

const TIER_COPY: Partial<Record<AccessLevel, { label: string; hint: string }>> =
  {
    comment: {
      hint: "Can't edit",
      label: "Comment only",
    },
    view: {
      hint: "Can't edit or comment",
      label: "View only",
    },
  };

export type AccessMarkerProps = Omittable<{
  /** The viewer's resolved tier on the object. `edit` and above render
   *  nothing — the marker only names a reach that stops short of editing. */
  level: AccessLevel | null | undefined;
  /** The server's edit decision (`viewerCanEdit`). It can grant edit past the
   *  displayed tier (an assignee on a view-shared project), and a viewer who
   *  can edit needs no marker whatever their tier says. */
  canEdit: boolean;
  /** Where the hint opens. @default "bottom" — the marker sits in a header
   *  row with the surface's content below it. */
  side?: React.ComponentProps<typeof TooltipPopup>["side"];
}>;

export function AccessMarker({
  level,
  canEdit,
  side = "bottom",
}: AccessMarkerProps): React.ReactElement | null {
  const copy = level == null ? undefined : TIER_COPY[level];
  if (canEdit || !copy) return null;
  return <AccessMarkerChip copy={copy} side={side} />;
}

function AccessMarkerChip({
  copy,
  side,
}: {
  copy: { label: string; hint: string };
  side: React.ComponentProps<typeof TooltipPopup>["side"];
}): React.ReactElement {
  const { ref, fits } = useFitsBeside<HTMLDivElement>();
  return (
    <Tooltip>
      {/* A marker is not a control: a plain wrapper, not a tab-stop button.
          The wrapper is what `useFitsBeside` measures, so it is the row's
          direct child. */}
      <TooltipTrigger
        render={<Box direction="row" align="center" shrink={false} ref={ref} />}
      >
        <Badge
          size="tag"
          tone="outline"
          aria-label={fits ? undefined : copy.label}
          role={fits ? undefined : "img"}
        >
          <Icon name="lock" size="xs" color="muted" />
          {fits ? copy.label : null}
        </Badge>
      </TooltipTrigger>
      {/* Collapsed to the lock, the tier is no longer on screen, so the
          tooltip leads with it. */}
      <TooltipPopup side={side}>
        {fits ? copy.hint : `${copy.label} · ${copy.hint}`}
      </TooltipPopup>
    </Tooltip>
  );
}
